import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { useAdminAuth } from "../context/AdminAuthContext";

const normalize = (value) => String(value || "").toLowerCase();
const getCommunityMemberCount = (community) => {
  if (!community || typeof community !== "object") return 0;
  if (typeof community.memberCount === "number") {
    return Math.max(0, community.memberCount);
  }
  if (Array.isArray(community.memberIds)) return community.memberIds.length;
  if (Array.isArray(community.members)) return community.members.length;
  return 0;
};

const Communities = () => {
  const { user: currentUser, role: currentRole } = useAdminAuth();
  const [communities, setCommunities] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [userProfiles, setUserProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteSummary, setBulkDeleteSummary] = useState(null);

  const loadCommunities = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, "communities"), orderBy("createdAt", "desc"), limit(200))
      );
      setCommunities(
        snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
      );
    } catch (error) {
      console.error("Failed to load communities", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCommunities();
  }, [loadCommunities]);

  const getUserLabel = useCallback(
    (userId, fallback) => {
      const profile = userId ? userProfiles[userId] : null;
      return (
        profile?.username ||
        profile?.name ||
        profile?.displayName ||
        fallback ||
        userId ||
        "Unknown"
      );
    },
    [userProfiles]
  );

  const filteredCommunities = useMemo(() => {
    if (!search.trim()) return communities;
    const q = normalize(search);
    return communities.filter((item) =>
      [item.name, item.ownerId, getUserLabel(item.ownerId), item.privacy].some(
        (value) => normalize(value).includes(q)
      )
    );
  }, [communities, getUserLabel, search]);

  const oneMemberCommunityCount = useMemo(
    () => communities.filter((community) => getCommunityMemberCount(community) === 1).length,
    [communities]
  );

  useEffect(() => {
    const ids = new Set();
    communities.forEach((community) => {
      if (community.ownerId) ids.add(community.ownerId);
    });
    requests.forEach((request) => {
      if (request.userId) ids.add(request.userId);
    });
    members.forEach((member) => {
      const memberId = member.userId || member.id;
      if (memberId) ids.add(memberId);
    });
    if (selectedCommunity?.ownerId) ids.add(selectedCommunity.ownerId);

    const missing = Array.from(ids).filter((id) => !(id in userProfiles));
    if (!missing.length) return () => {};

    let isMounted = true;
    const loadProfiles = async () => {
      try {
        const docs = await Promise.all(
          missing.map((uid) => getDoc(doc(db, "users", uid)))
        );
        if (!isMounted) return;
        const next = docs.reduce((acc, snap, index) => {
          acc[missing[index]] = snap.exists() ? snap.data() : null;
          return acc;
        }, {});
        if (Object.keys(next).length) {
          setUserProfiles((prev) => ({ ...prev, ...next }));
        }
      } catch (error) {
        console.error("Failed to load user profiles", error);
      }
    };

    loadProfiles();

    return () => {
      isMounted = false;
    };
  }, [communities, members, requests, selectedCommunity?.ownerId, userProfiles]);

  const loadDetails = async (community) => {
    if (!community) return;
    setLoadingDetails(true);
    try {
      const membersSnap = await getDocs(
        query(
          collection(db, "communities", community.id, "members"),
          orderBy("joinedAt", "desc"),
          limit(50)
        )
      );
      setMembers(
        membersSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
      );

      const requestsSnap = await getDocs(
        query(
          collection(db, "communities", community.id, "joinRequests"),
          orderBy("requestedAt", "desc"),
          limit(50)
        )
      );
      setRequests(
        requestsSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
      );
    } catch (error) {
      console.error("Failed to load community details", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSelect = (community) => {
    setSelectedCommunity(community);
    loadDetails(community);
  };

  const updateRequestStatus = async (requestId, status) => {
    if (!selectedCommunity) return;
    try {
      const requestRef = doc(
        db,
        "communities",
        selectedCommunity.id,
        "joinRequests",
        requestId
      );
      await updateDoc(requestRef, {
        status,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid || null,
        updatedByRole: currentRole || null,
      });

      setRequests((prev) =>
        prev.map((request) =>
          request.id === requestId ? { ...request, status } : request
        )
      );
    } catch (error) {
      console.error("Failed to update join request", error);
    }
  };

  const handleBulkDeleteOneMemberCommunities = async () => {
    if (bulkDeleting) return;

    const confirmed = window.confirm(
      "Delete all communities with exactly 1 member? This action cannot be undone."
    );
    if (!confirmed) return;

    setBulkDeleting(true);
    setBulkDeleteSummary(null);

    try {
      const callDelete = httpsCallable(functions, "deleteOneMemberCommunities");
      const response = await callDelete({
        maxDeletes: 500,
      });
      const summary = response?.data || {};
      setBulkDeleteSummary(summary);

      const deletedIds = Array.isArray(summary.deletedIds) ? summary.deletedIds : [];
      if (deletedIds.length) {
        const deletedSet = new Set(deletedIds.map((id) => String(id)));
        setCommunities((prev) =>
          prev.filter((community) => !deletedSet.has(String(community.id)))
        );
        setSelectedCommunity((prev) =>
          prev && deletedSet.has(String(prev.id)) ? null : prev
        );
        if (selectedCommunity && deletedSet.has(String(selectedCommunity.id))) {
          setMembers([]);
          setRequests([]);
        }
      }

      if (summary?.failedCount > 0) {
        console.error("Some communities failed to delete", summary.failed || []);
      }
    } catch (error) {
      console.error("Failed to bulk delete communities", error);
      alert(error?.message || "Failed to bulk delete communities.");
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Communities</h1>
          <p>Review membership requests and maintain community quality.</p>
        </div>
        <div className="page-actions">
          <input
            className="search-input"
            type="search"
            placeholder="Search by name, owner, or privacy"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <button
            type="button"
            className="button danger"
            disabled={bulkDeleting || loading}
            onClick={handleBulkDeleteOneMemberCommunities}
          >
            {bulkDeleting
              ? "Deleting..."
              : `Delete 1-member communities (${oneMemberCommunityCount})`}
          </button>
        </div>
      </div>

      {bulkDeleteSummary ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="detail-body">
            <div className="detail-row">
              <div className="detail-label">Bulk delete summary</div>
              <div className="detail-value">
                Deleted {bulkDeleteSummary.deletedCount || 0} /{" "}
                {bulkDeleteSummary.targetCount || 0}
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-label">Scanned</div>
              <div className="detail-value">{bulkDeleteSummary.scannedCount || 0}</div>
            </div>
            <div className="detail-row">
              <div className="detail-label">Failed</div>
              <div className="detail-value">{bulkDeleteSummary.failedCount || 0}</div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid-2">
        <div className="card table-card">
          <div className="card-header">
            <h3>Communities</h3>
            <span className="chip">{filteredCommunities.length} total</span>
          </div>
          {loading ? (
            <div className="empty-inline">Loading communities...</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Owner</th>
                    <th>Privacy</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCommunities.map((item) => (
                    <tr
                      key={item.id}
                      className={
                        selectedCommunity?.id === item.id ? "active-row" : ""
                      }
                      onClick={() => handleSelect(item)}
                    >
                      <td>
                        <div className="table-title">{item.name || "Untitled"}</div>
                        <div className="table-subtitle">{item.id}</div>
                      </td>
                      <td title={item.ownerId || ""}>
                        {getUserLabel(item.ownerId)}
                      </td>
                      <td>
                        <span className="status-pill">
                          {item.privacy || "Public"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card detail-card">
          <div className="card-header">
            <h3>Community details</h3>
          </div>
          {!selectedCommunity ? (
            <div className="empty-inline">Select a community to view details.</div>
          ) : loadingDetails ? (
            <div className="empty-inline">Loading details...</div>
          ) : (
            <div className="detail-body">
              <div className="detail-row">
                <div className="detail-label">Name</div>
                <div className="detail-value">
                  {selectedCommunity.name || "N/A"}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Owner</div>
                <div className="detail-value" title={selectedCommunity.ownerId || ""}>
                  {getUserLabel(selectedCommunity.ownerId)}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Privacy</div>
                <div className="detail-value">
                  {selectedCommunity.privacy || "Public"}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Members</div>
                <div className="detail-value">{members.length}</div>
              </div>

              <div className="divider" />

              <div className="section-title">Join requests</div>
              {requests.length === 0 ? (
                <div className="empty-inline">No join requests.</div>
              ) : (
                <div className="list">
                  {requests.map((request) => (
                    <div className="list-row" key={request.id}>
                      <div>
                        <div className="list-title">
                          {getUserLabel(request.userId, request.username)}
                        </div>
                        <div className="list-subtitle">
                          {request.status || "pending"}
                        </div>
                      </div>
                      <div className="inline-actions">
                        <button
                          className="button primary"
                          type="button"
                          onClick={() =>
                            updateRequestStatus(request.id, "Approved")
                          }
                        >
                          Approve
                        </button>
                        <button
                          className="button danger"
                          type="button"
                          onClick={() =>
                            updateRequestStatus(request.id, "Rejected")
                          }
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="divider" />

              <div className="section-title">Recent members</div>
              {members.length === 0 ? (
                <div className="empty-inline">No members yet.</div>
              ) : (
                <div className="list">
                  {members.slice(0, 6).map((member) => (
                    <div className="list-row" key={member.id}>
                      <div>
                        <div className="list-title">
                          {getUserLabel(member.userId || member.id)}
                        </div>
                        <div className="list-subtitle">
                          {member.role || "Member"}
                        </div>
                      </div>
                      <div className="list-meta">Joined</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Communities;
